import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import AdminLayout from '../components/AdminLayout';
import './ActivityLog.css';

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ actionType: '', adminEmail: '' });

  useEffect(() => {
    loadLogs();
  }, []);

  async function loadLogs() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase
        .rpc('get_admin_activity_logs', {
          p_limit: 100,
          p_offset: 0,
          p_action_type: filter.actionType || null,
          p_admin_email: filter.adminEmail || null
        });

      if (rpcError) throw rpcError;

      setLogs(data || []);
    } catch (err) {
      console.error('Error loading activity logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getActionIcon(actionType) {
    switch (actionType) {
      case 'DELETE_CONTRACTOR':
        return '🗑️';
      case 'DELETE_USER':
        return '👤';
      case 'VIEW_CONTRACTORS':
        return '👁️';
      default:
        return '📝';
    }
  }

  function getActionColor(actionType) {
    switch (actionType) {
      case 'DELETE_CONTRACTOR':
      case 'DELETE_USER':
        return 'red';
      case 'VIEW_CONTRACTORS':
        return 'blue';
      default:
        return 'gray';
    }
  }

  function formatActionType(actionType) {
    return actionType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  function formatDetails(details) {
    if (!details) return null;

    const entries = Object.entries(details);
    if (entries.length === 0) return null;

    return (
      <div className="log-details">
        {entries.map(([key, value]) => (
          <div key={key} className="log-detail-item">
            <span className="log-detail-key">{key.replace(/_/g, ' ')}:</span>
            <span className="log-detail-value">{JSON.stringify(value)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="activity-log-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading activity logs...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="activity-log-container">
      <div className="activity-log-header">
        <h1>Admin Activity Log</h1>
        <p>Audit trail of all super admin actions</p>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="activity-log-filters">
        <div className="filter-group">
          <label>Filter by Action:</label>
          <select
            value={filter.actionType}
            onChange={(e) => setFilter({ ...filter, actionType: e.target.value })}
          >
            <option value="">All Actions</option>
            <option value="DELETE_CONTRACTOR">Delete Contractor</option>
            <option value="DELETE_USER">Delete User</option>
            <option value="VIEW_CONTRACTORS">View Contractors</option>
          </select>
        </div>

        <button className="btn-filter" onClick={loadLogs}>
          Apply Filters
        </button>

        <button className="btn-refresh" onClick={loadLogs}>
          🔄 Refresh
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="empty-state">
          <p>No activity logs found</p>
        </div>
      ) : (
        <div className="logs-list">
          {logs.map(log => (
            <div key={log.id} className={`log-card log-${getActionColor(log.action_type)}`}>
              <div className="log-icon">
                {getActionIcon(log.action_type)}
              </div>

              <div className="log-content">
                <div className="log-header-row">
                  <h3 className="log-action">{formatActionType(log.action_type)}</h3>
                  <span className="log-time">
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="log-meta">
                  <span className="log-admin">
                    Admin: <strong>{log.admin_email}</strong>
                  </span>
                  {log.target_name && (
                    <span className="log-target">
                      Target: <strong>{log.target_name}</strong>
                    </span>
                  )}
                  {log.target_type && (
                    <span className="log-type">
                      Type: {log.target_type}
                    </span>
                  )}
                </div>

                {log.details && formatDetails(log.details)}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
